import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Form, Button } from 'react-bootstrap';
import 'bootstrap/dist/css/bootstrap.min.css';
import '../CSS/Details.css';
import apiClient from '../lib/api-client';
import Logo from '../assets/icon.svg';
import femaRegionsMap from '../assets/fema_regions_map.png';
import { useAuth } from '../AuthContext';

const FemaReportConsent = () => {
  const navigate = useNavigate();
  const { checkAuth } = useAuth();
  const [isLoading, setIsLoading] = useState(false);
  const [regions, setRegions] = useState([]);
  const [selectedRegions, setSelectedRegions] = useState([]);
  const [scenario, setScenario] = useState('mean');

  useEffect(() => {
    const fetchRegions = async () => {
      try {
        const data = await apiClient.getFEMARegions();
        setRegions(data);
      } catch (e) {
        console.error('Failed to fetch FEMA regions', e);
        setRegions(
          Array.from({ length: 10 }, (_, i) => ({ id: i + 1, name: '' }))
        );
      }
    };

    fetchRegions();
  }, []);

  const toggleRegion = (id) => {
    setSelectedRegions(prev =>
      prev.includes(id) ? prev.filter(r => r !== id) : [...prev, id]
    );
  };

  const toggleAllRegions = () => {
    if (selectedRegions.length === regions.length) {
      setSelectedRegions([]);
    } else {
      setSelectedRegions(regions.map(r => r.id));
    }
  };

  const allSelected = regions.length > 0 && selectedRegions.length === regions.length;

  const handleSubscribe = async () => {
    if (selectedRegions.length === 0) return;

    setIsLoading(true);
    try {
      await apiClient.savePdfSubscription({
        fema_region_ids: selectedRegions,
        default_report_scenario: scenario,
        is_subscribed: true,
      });

      await checkAuth();
      navigate('/', { replace: true });
    } catch (error) {
      console.error('Failed to save PDF subscription:', error);
      alert(error.message || 'Failed to save your preferences. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  const handleSkip = async () => {
    setIsLoading(true);
    try {
      await apiClient.savePdfSubscription({
        is_subscribed: false,
      });

      await checkAuth();
      navigate('/', { replace: true });
    } catch (error) {
      console.error('Failed to save PDF subscription skip:', error);
      // Even if the skip call fails, let the user proceed
      navigate('/', { replace: true });
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="auth-container d-flex align-items-center justify-content-center">
      <div className="auth-card" style={{ maxWidth: '1100px' }}>
        {/* Header */}
        <div className="d-flex align-items-center justify-content-center mb-2 p-2">
          <img src={Logo} alt="RECOVER Logo" className="me-3" style={{ height: '60px' }} />
          <div className="text-center">
            <h2 className="text-primary mb-1">FEMA Region Email Alerts</h2>
            <p className="text-main mb-0">Would you like to receive PDF forecast reports via email?</p>
          </div>
        </div>

        {/* Content */}
        <div className="p-2">
          <p className="text-main mb-2">
            We generate PDF reports by FEMA region covering 7-day outage forecasts, estimated restoration times, alerts, and weather insights. Subscribe to the regions you're interested in, and if we anticipate a storm likely to cause significant outages, we'll send an early alert.
          </p>
          <p className="text-muted small mb-3">
            {/* Secondary description placeholder — fill in manually */}
          </p>

          <div className="row">
            {/* Left column — form */}
            <div className="col-md-6">
              <Form>
                <Form.Group className="mb-3">
                  <Form.Label className="fw-semibold">Select FEMA Regions</Form.Label>
                  <div className="border rounded p-2" style={{ maxHeight: '220px', overflowY: 'auto' }}>
                    <Form.Check
                      type="checkbox"
                      id="consent-region-all"
                      label={<span className="fw-semibold">All Regions</span>}
                      checked={allSelected}
                      onChange={toggleAllRegions}
                      className="mb-2 pb-2 border-bottom"
                    />
                    {regions.map((r) => (
                      <Form.Check
                        key={r.id}
                        type="checkbox"
                        id={`consent-region-${r.id}`}
                        label={`Region ${r.id}${r.name ? ` \u2014 ${r.name}` : ''}`}
                        checked={selectedRegions.includes(r.id)}
                        onChange={() => toggleRegion(r.id)}
                        className="mb-1"
                      />
                    ))}
                  </div>
                </Form.Group>

                <Form.Group className="mb-3">
                  <Form.Label className="fw-semibold">Default Report Scenario</Form.Label>
                  <Form.Select
                    value={scenario}
                    onChange={(e) => setScenario(e.target.value)}
                  >
                    <option value="min">Minimum (Best-case)</option>
                    <option value="mean">Mean (Expected)</option>
                    <option value="max">Maximum (Worst-case)</option>
                  </Form.Select>
                </Form.Group>
              </Form>

              <div className="d-flex gap-3 mt-4">
                <Button
                  variant="primary"
                  onClick={handleSubscribe}
                  disabled={isLoading || selectedRegions.length === 0}
                  className="px-4"
                >
                  {isLoading ? (
                    <>
                      <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                      Processing...
                    </>
                  ) : (
                    <>
                      <i className="fas fa-bell me-2"></i>
                      Subscribe
                    </>
                  )}
                </Button>
                <Button
                  variant="outline-secondary"
                  onClick={handleSkip}
                  disabled={isLoading}
                  className="px-4"
                >
                  Skip for now
                </Button>
              </div>
            </div>

            {/* Right column — reference map */}
            <div className="col-md-6 d-flex flex-column align-items-center justify-content-start">
              <p className="text-muted small mb-2">FEMA Regions Reference Map</p>
              <img
                src={femaRegionsMap}
                alt="FEMA Regions Map showing all 10 regions across the United States"
                className="img-fluid rounded border"
                style={{ maxWidth: '100%' }}
              />
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default FemaReportConsent;
